// Vendor Libs
import React, {useContext, useState} from 'react';
import PropTypes from 'prop-types';

// Material UI
import {AppBar, IconButton, Toolbar} from '@material-ui/core';
import {makeStyles, useTheme} from '@material-ui/core/styles';

// Dish Tech React
import {Logout} from '@dishtech-react/core';

// Contexts
import {RouteContext} from '../../../contexts/route-context';
import {LayoutContext} from '../../../contexts/layout-context';

// Images
import logo from '../../../images/dobby-logo.svg';

const useStyles = makeStyles(theme => ({
  root: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  menuButton: {
    marginLeft: -8,
    marginRight: 8,
  },
  logo: {
    height: '40px',
    margin: '8px',
  },
  span: {
    flexGrow: 1,
    width: '100%',
  },
}));

const createGet = (
  {clipped, navVariant, collapsible, collapsed, open, squeezed, navAnchor},
  normal,
  shrink,
  pushed,
  unsqueeze
) => () => {
  if (clipped || navAnchor !== 'left') return normal;
  if (navVariant === 'persistent' && open) {
    if (squeezed) {
      return pushed;
    }
    return unsqueeze;
  }
  if (navVariant === 'permanent') {
    if (collapsible) {
      if (collapsed) return shrink;
      return pushed;
    }
    return pushed;
  }
  return normal;
};

const Header = ({className, children, menuIcon, style, toolbarProps, ...props}) => {
  const classes = useStyles();
  const theme = useTheme();
  const layoutContext = useContext(LayoutContext);
  const {clipped, collapsedWidth, navWidth, navVariant, headerPosition, open, setOpen} = layoutContext;
  const getWidth = createGet(
    layoutContext,
    '100%',
    `calc(100% - ${collapsedWidth}px)`,
    `calc(100% - ${navWidth}px)`,
    '100%'
  );
  const getMargin = createGet(layoutContext, 0, collapsedWidth, navWidth, navWidth);
  const shouldRenderMenu = navVariant !== 'permanent' && !!menuIcon;
  const routeContext = useContext(RouteContext);
  const [isLogoutOpen, setLogoutOpen] = useState(false);

  return (
    <AppBar
      color="default"
      elevation={0}
      {...props}
      className={`${className} ${classes.root}`}
      position={headerPosition}
      style={{
        ...style,
        zIndex: clipped ? theme.zIndex.drawer + 1 : theme.zIndex.appBar,
        width: getWidth(),
        marginLeft: getMargin(),
        boxShadow: '0 5px 4px -4px #8a8987',
      }}
    >
      <Toolbar {...toolbarProps}>
        {shouldRenderMenu && (
          <IconButton className={classes.menuButton} onClick={() => setOpen(!open)}>
            {open ? menuIcon.active : menuIcon.inactive || menuIcon.active}
          </IconButton>
        )}
        <img alt="dobby logo" className={classes.logo} src={logo} />
        {typeof children === 'function' ? children(layoutContext) : children}
        <span className={classes.span} />
        <Logout
          isLogoutOpen={isLogoutOpen}
          logoutAllApps={false}
          routeContext={routeContext}
          setLogoutOpen={setLogoutOpen}
          showIcon={true}
          textCaseUpper={false}
        />
      </Toolbar>
    </AppBar>
  );
};

Header.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  className: PropTypes.string,
  menuIcon: PropTypes.shape({
    active: PropTypes.node,
    inactive: PropTypes.node.isRequired,
  }),
  position: PropTypes.string,
  style: PropTypes.shape({}),
  toolbarProps: PropTypes.shape({}),
};

Header.defaultProps = {
  className: '',
  style: {},
  position: 'sticky',
  toolbarProps: {},
  menuIcon: null,
};

export default Header;
